// src/components/auth/registration/VerificationStep.jsx
import React from 'react';
import Button from '../../common/Button';

const VerificationStep = ({ data, isSubmitting, handleSubmit, prevStep }) => {
  // Render a section of the summary
  const SummarySection = ({ title, children }) => (
    <div className="mb-4">
      <h4 className="text-sm font-medium text-gray-500">{title}</h4>
      <div className="mt-1 text-md text-gray-900">
        {children}
      </div>
    </div>
  );
  
  // Get brand names
  const getBrandNames = () => {
    const brandObjects = [
      ...(data.brandObjects || []),
      ...(data.customBrands || [])
    ];
    
    return brandObjects
      .map(brand => brand.name)
      .join(', ');
  };
  
  // Get category names
  const getCategoryNames = () => {
    const categoryObjects = data.categoryObjects || [];
    
    return categoryObjects
      .map(category => category.name)
      .join(', ');
  };
  
  return (
    <div className="mt-8 space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Verify Your Information
        </h3>
        <p className="text-sm text-gray-500 mb-6">
          Please review your registration details before submitting. You can go back to make any changes.
        </p>
        
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          {/* Personal Information */}
          <div className="mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-3">Personal Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SummarySection title="Shop Name">
                {data.shopName}
              </SummarySection>
              
              <SummarySection title="Contact Person">
                {data.contactPersonName}
              </SummarySection>
              
              <SummarySection title="Phone Number">
                {data.phone}
              </SummarySection>
              
              <SummarySection title="Email">
                {data.email}
              </SummarySection>
            </div>
          </div>
          
          {/* Address Information */}
          <div className="mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-3">Address Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SummarySection title="Address">
                {data.street}, {data.city}, {data.state} {data.zip}
              </SummarySection>
              
              {data.location && (
                <SummarySection title="Location Coordinates">
                  Lat: {data.location.latitude.toFixed(6)}, 
                  Long: {data.location.longitude.toFixed(6)}
                </SummarySection>
              )}
            </div>
          </div>
          
          {/* Categories Information */}
          <div className="mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-3">Product Categories</h4>
            <SummarySection title="Selected Categories">
              {getCategoryNames() || 'None selected'}
            </SummarySection>
          </div>
          
          {/* Brands Information */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3">Product Brands</h4>
            <SummarySection title="Selected Brands">
              {getBrandNames() || 'None selected'}
            </SummarySection>
          </div>
        </div>
        
        <div className="mt-6 text-sm text-gray-500">
          <p>
            By clicking "Complete Registration", you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
      
      <div className="flex justify-between mt-6">
        <Button type="button" variant="secondary" onClick={prevStep} disabled={isSubmitting}>
          Previous
        </Button>
        <Button 
          type="button" 
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <div className="flex items-center">
              <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              Processing...
            </div>
          ) : (
            'Complete Registration'
          )}
        </Button>
      </div>
    </div>
  );
};

export default VerificationStep;